import { useEffect, useState } from 'react';
import { readAssetValue } from '@chainflip/utils/chainflip';
import { abbreviate, isHex } from '@chainflip/utils/string';
import * as Sentry from '@sentry/react';
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferInstruction,
  getAccount,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import {
  useWallet as useSolanaWallet,
  useConnection as useSolanaConnection,
} from '@solana/wallet-adapter-react';
import { PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { erc20Abi, getAddress } from 'viem';
import {
  useAccount as useEvmAccount,
  useDisconnect as useEvmDisconnect,
  useWalletClient as useEvmWalletClient,
} from 'wagmi';
import { isEvmChain, isSolanaChain, isTronChain, parseEvmChainId } from '@/shared/assets/chains';
import { BoostBolt } from '@/shared/assets/frames/BoostBolt';
import { NATIVE_TOKEN_ADDRESS } from '@/shared/assets/tokens/constants';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import Button from '@/shared/components/flip-ui-kit/Button';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { QRCode } from '@/shared/components/QRCode';
import TokenInputField from '@/shared/components/TokenInputField';
import { executeTronTransfer, useTronWallet } from '@/shared/components/TronConnect';
import WalletActionButton from '@/shared/components/WalletActionButton';
import useBoolean from '@/shared/hooks/useBoolean';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useTokenBalance from '@/shared/hooks/useTokenBalance';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import QRIcon from '@/shared/icons/flip-ui-kit/large/QR';
import WalletIcon from '@/shared/icons/flip-ui-kit/large/Wallet';
import AlarmWarningIcon from '@/shared/icons/flip-ui-kit/small/AlarmWarning';
import InformationIcon from '@/shared/icons/flip-ui-kit/small/Information';
import ShuffleIcon from '@/shared/icons/flip-ui-kit/small/Shuffle';
import { formatTimeUntilExpiry } from '@/shared/utils/date';
import { getChainflipNetwork } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';
import { DepositInformation } from './DepositInformation';
import { useDepositExpiryTime } from '../../../hooks/useDepositExpiryTime';
import useIngressEgressEnv from '../../../hooks/useEnvironment';
import type { DepositChannel } from '../../../hooks/useStateChainAccount';

const expiryBuffer = getChainflipNetwork() === 'mainnet' ? 10 * 60 : 60; // 10 minutes on mainnet, 1 minute on testnet

export const DepositChannelInfo = ({
  onClose,
  onDepositSent,
  onTokenClick,
  channel,
}: {
  onClose: () => void;
  onDepositSent: (amount: TokenAmount) => void;
  onTokenClick: () => void;
  channel: DepositChannel;
}) => {
  const chainflipAsset = channel.asset.chainflipId;
  const [inputAmount, setInputAmount] = useState<TokenAmount>();
  const [inputError, setInputError] = useState<string>();
  useEffect(() => {
    setInputError(undefined);
  }, [inputAmount]);

  const { balance: connectedWalletBalance } = useTokenBalance(channel.asset);
  const {
    disconnect: disconnectSolanaWallet,
    publicKey: solanaPublicKey,
    sendTransaction: sendSolanaTransaction,
    connected: isSolanaWalletConnected,
  } = useSolanaWallet();
  const { connection: solanaConnection } = useSolanaConnection();
  const { chain: connectedEvmChain } = useEvmAccount();
  const { disconnect: disconnectEvmWallet } = useEvmDisconnect();
  const { data: evmWalletClient } = useEvmWalletClient();
  const tronWallet = useTronWallet();
  const channelEvmChainId = parseEvmChainId(channel.asset.chain.id);
  const isEvmWalletConnected = connectedEvmChain?.id === channelEvmChainId;
  const isTronWalletConnected = isTronChain(channel.asset.chain.id) && tronWallet.isConnected;
  const isWalletConnected =
    (isEvmChain(channel.asset.chain.id) && isEvmWalletConnected) ||
    (isSolanaChain(channel.asset.chain.id) && isSolanaWalletConnected) ||
    isTronWalletConnected;
  // for evm-native chainflip accounts, we need to control the showing of the qr
  // code via manual toggle rather than disconnecting/reconnecting wallets
  const { value: shouldShowQrCode, setTrue: showQrCode, setFalse: hideQrCode } = useBoolean(false);

  const { expiryTime, isLoading: expiryTimeLoading } = useDepositExpiryTime(channel);
  const { ingressFees, minimumDepositAmounts, isLoading: isEnvLoading } = useIngressEgressEnv();
  const { selectedAccount } = usePolkadot();

  const minimumDepositAmount = new TokenAmount(
    minimumDepositAmounts ? readAssetValue(minimumDepositAmounts, chainflipAsset) : 0n,
    channel.asset.decimals,
  );

  const ingressFee = new TokenAmount(
    ingressFees ? (readAssetValue(ingressFees, chainflipAsset) ?? 0n) : 0n,
    channel.asset.decimals,
  );

  const depositAddress =
    isEvmChain(channel.asset.chain.id) && !isTronChain(channel.asset.chain.id)
      ? getAddress(channel.depositAddress) // display checksummed address for evm chains
      : channel.depositAddress;

  const {
    mutate: sendDepositTransaction,
    isPending: isSendingTransaction,
    isSuccess: transactionSent,
  } = useMutation({
    mutationFn: async () => {
      let hadError = false;

      if (!inputAmount || inputAmount.eq(0)) {
        setInputError('A deposit amount is required');
        hadError = true;
      } else if (inputAmount.lt(minimumDepositAmount)) {
        setInputError('Amount is lower than minimum amount');
        hadError = true;
      } else if (inputAmount.lt(ingressFee)) {
        setInputError('Amount is lower than deposit fee');
        hadError = true;
      }

      if (hadError) return Promise.reject();

      if (isTronChain(channel.asset.chain.id)) {
        if (!tronWallet.address || !window.tronWeb) throw new Error('TronLink is not connected');

        await executeTronTransfer({
          tronWeb: window.tronWeb,
          amount: inputAmount!.toBigInt(),
          depositAddress,
          fromAddress: tronWallet.address,
          tokenAddress: channel.asset.address,
        });

        onDepositSent(inputAmount!);
      } else if (isEvmChain(channel.asset.chain.id)) {
        if (!evmWalletClient) throw new Error('Missing evm wallet for sending deposit transaction');
        if (!isHex(depositAddress))
          throw new Error(`Invalid evm deposit address: ${depositAddress}`);

        await (channel.asset.address === NATIVE_TOKEN_ADDRESS
          ? evmWalletClient.sendTransaction({
              value: inputAmount!.toBigInt(),
              to: depositAddress,
            })
          : evmWalletClient.writeContract({
              address: channel.asset.address as `0x${string}`,
              abi: erc20Abi,
              functionName: 'transfer',
              args: [depositAddress, inputAmount!.toBigInt()],
            }));

        onDepositSent(inputAmount!);
      } else if (isSolanaChain(channel.asset.chain.id)) {
        if (!solanaPublicKey)
          throw new Error('Missing solana public key for sending deposit transaction');

        const tx = new Transaction();
        const depositPublicKey = new PublicKey(depositAddress);

        if (channel.asset.chainflipId === 'Sol') {
          tx.add(
            SystemProgram.transfer({
              fromPubkey: solanaPublicKey,
              toPubkey: depositPublicKey,
              lamports: inputAmount!.toBigInt(),
            }),
          );
        } else {
          const mint = new PublicKey(channel.asset.address);
          const associatedTokenFrom = await getAssociatedTokenAddress(mint, solanaPublicKey);
          const fromAccount = await getAccount(solanaConnection, associatedTokenFrom);
          const associatedTokenTo = await getAssociatedTokenAddress(mint, depositPublicKey, true);

          tx.add(
            createAssociatedTokenAccountIdempotentInstruction(
              solanaPublicKey,
              associatedTokenTo,
              depositPublicKey,
              mint,
            ),
          );
          tx.add(
            createTransferInstruction(
              fromAccount.address,
              associatedTokenTo,
              solanaPublicKey,
              inputAmount!.toBigInt(),
            ),
          );
        }
        await sendSolanaTransaction(tx, solanaConnection);

        onDepositSent(inputAmount!);
      }
    },
    onError(err) {
      if (
        err.name === 'TransactionExecutionError' &&
        err.message.includes('User rejected the request')
      ) {
        return; // user rejected ethereum tx
      }

      if (
        err.name === 'WalletSendTransactionError' &&
        err.message.includes('User rejected the request')
      ) {
        return; // user rejected solana tx
      }

      toast.error('Failed to send transaction', { description: err.message });
      Sentry.captureException(err);
    },
  });

  let chainWalletLabel;
  if (isTronChain(channel.asset.chain.id)) {
    chainWalletLabel = 'TronLink';
  } else if (isEvmChain(channel.asset.chain.id)) {
    chainWalletLabel = 'EVM';
  } else {
    chainWalletLabel = 'Solana';
  }

  return (
    <div className="flex w-[487px] flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2 font-aeonikMedium text-24 text-cf-white">
          <span>Send</span>
          <TokenWithChainByAsset asset={chainflipAsset} size="medium" />
          <span>
            {channel.asset.symbol} on {channel.asset.chain.name}
          </span>
        </div>
        <button type="button" onClick={onClose} className="outline-cf-gray-5">
          <CloseIcon />
        </button>
      </div>
      <div className="flex flex-col gap-5">
        {!isWalletConnected || shouldShowQrCode ? (
          <div className="flex flex-col gap-6">
            <div
              className="rounded-md border border-cf-white/70 bg-white p-4"
              style={{
                background: 'linear-gradient(150deg, #F2F2F2 0%, #CDCDCD 100%)',
                boxShadow:
                  '3.309px 2.647px 18.5px 0px rgba(0, 0, 0, 0.37) inset, 0px 2.647px 13.237px 0px rgba(0, 0, 0, 0.50)',
              }}
            >
              <QRCode
                content={depositAddress}
                width={132}
                icon={
                  channel.boostFee ? (
                    <BoostBolt />
                  ) : (
                    <ChainLogo
                      chain={channel.asset.chain}
                      variant="transparent"
                      props={{ width: 20, height: 20 }}
                    />
                  )
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-x-1.5 font-aeonikMedium text-12 text-cf-light-3">
                <ChainLogo
                  chain={channel.asset.chain}
                  variant="monochromatic"
                  props={{ height: 14 }}
                  size="medium"
                />
                {channel.asset.chain.name} Network
              </div>
              <div className="flex items-center gap-2">
                <Tooltip content={depositAddress}>
                  <div className="font-aeonikMedium text-16 text-cf-white">
                    {abbreviate(depositAddress, 8)}
                  </div>
                </Tooltip>
                <CopyButton textToCopy={depositAddress} />
              </div>
            </div>
          </div>
        ) : (
          <TokenInputField
            token={channel.asset}
            value={inputAmount}
            error={inputError}
            onChange={setInputAmount}
            balance={connectedWalletBalance}
            onTokenClick={onTokenClick}
          />
        )}
        <DepositInformation channel={channel} showMinDepositAmount showExplorerLink />
        <div className="flex flex-col gap-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2">
          <div className="flex items-center gap-3 text-cf-orange-2">
            <AlarmWarningIcon className="shrink-0" />
            <span>
              Expires in:{' '}
              {expiryTimeLoading ? (
                <SkeletonLine count={1} width={50} className="ml-1" />
              ) : (
                formatTimeUntilExpiry(expiryTime - expiryBuffer)
              )}
            </span>
          </div>
          <div className="flex items-center gap-3 leading-4">
            <InformationIcon className="shrink-0" />
            <span>
              Send at least{' '}
              {isEnvLoading ? (
                <SkeletonLine count={1} width={50} className="ml-1" />
              ) : (
                <span className="text-cf-white">
                  {minimumDepositAmount?.toFormatted()} {channel.asset.symbol} on{' '}
                  {channel.asset.chain.name}
                </span>
              )}{' '}
              or the funds will be lost.
            </span>
          </div>
          <div className="flex items-center gap-3 leading-4">
            <ShuffleIcon className="shrink-0" />
            <span>
              <span className="text-cf-white">
                Do not deposit directly from a centralised exchange.
              </span>{' '}
              Delays may cause the address to expire, and your funds will be lost.
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {(isEvmChain(channel.asset.chain.id) || isSolanaChain(channel.asset.chain.id)) && (
          <WalletActionButton
            key={String(isWalletConnected && shouldShowQrCode)} /* do not animate color change */
            requireConnection
            chainId={channel.asset.chain.id}
            onClick={() => {
              if (isWalletConnected && shouldShowQrCode) {
                hideQrCode();
              } else {
                sendDepositTransaction();
              }
            }}
            loading={(isSendingTransaction || transactionSent) && 'iconOnly'}
            disabled={isSendingTransaction || transactionSent}
            type={
              isWalletConnected && !shouldShowQrCode ? 'primary-standard' : 'secondary-standard'
            }
            fullWidth
            connectWalletContent={
              <div className="flex items-center justify-center gap-x-1">
                <WalletIcon />
                <span>Connect {chainWalletLabel} wallet</span>
              </div>
            }
            switchNetworkContent={
              <div className="flex items-center justify-center gap-x-1">
                <WalletIcon />
                <span>Switch {chainWalletLabel} network</span>
              </div>
            }
          >
            {shouldShowQrCode ? (
              <div className="flex items-center justify-center gap-x-1">
                <WalletIcon />
                <span>Deposit from {chainWalletLabel} wallet</span>
              </div>
            ) : (
              'Confirm deposit'
            )}
          </WalletActionButton>
        )}
        {isWalletConnected && !shouldShowQrCode && (
          <Button
            className="flex w-full items-center justify-center gap-x-1 "
            type="secondary-standard"
            onClick={() => {
              if (isTronChain(channel.asset.chain.id)) {
                tronWallet.disconnect();
              } else if (isEvmChain(channel.asset.chain.id)) {
                if (selectedAccount?.type === 'evm') {
                  showQrCode();
                } else {
                  disconnectEvmWallet();
                }
              } else if (isSolanaChain(channel.asset.chain.id)) {
                disconnectSolanaWallet();
              }
            }}
          >
            <QRIcon />
            <span>Show deposit address</span>
          </Button>
        )}
      </div>
    </div>
  );
};
